import { Sparkles, ChevronUp, Clock, Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateCart } from '~/api';
import { useGetAdditionalProductsFromSeller } from '~/api/inventory';
import type {
  AdditionalProductsFromSellerQuery,
  LogisticsIncoterm,
} from '~/graphql/graphql';
import {
  buildCartItemUpdateInput,
  getVehiclePayload,
  getPlatformProductDimensions,
  partialLoadFraction,
  roundUpToBundleQty,
} from '~/utils/order';
import { toStandardUnits } from '~/utils/unitConversion';
import { formatCurrency, formatLengthWithBasis } from '~/utils';
import { getPropertyStringSafe } from '~/utils/properties';
import type { TransportMode } from '~/hooks/useTransportState';
import { CartCatalogAddRow, type CartLine } from './CartCatalogAddRow';

type SellerInventoryItem = NonNullable<
  NonNullable<
    AdditionalProductsFromSellerQuery['additionalProductsFromSeller']
  >['content'][number]
>;

interface AdditionalItemsProps {
  cartId: string;
  cartItems: CartLine[];
  locationId: string;
  locationName: string;
  sellerOrganizationId: string;
  sellerOrganizationFacilityId: string;
  // Undefined when no usable vehicle is selected; labels fall back to transport-agnostic copy.
  transport: TransportMode | undefined;
  totalLoads: number;
  selectedTransportVehicleId: string;
  parentOrderId?: string | null;
  incoTerm?: LogisticsIncoterm | null;
  cartCurrency?: string | null;
  hasShippingRoute?: boolean;
}

function calcFillQty(
  sellerItem: SellerInventoryItem,
  selectedTransportVehicleId: string,
  totalLoads: number
): number {
  const p = sellerItem.platformProductsLength?.platformProduct;
  const l = sellerItem.platformProductsLength?.length;
  if (!p) return 0;

  const vehiclePayload = getVehiclePayload(
    sellerItem.platformProductsLength?.platformProductVehicleCapacities,
    selectedTransportVehicleId
  );
  if (!vehiclePayload?.value) return 0;

  const fillFraction = partialLoadFraction(totalLoads) || 1.0;

  // Use the display unit (USER_PREFERRED) that the quantity is already in.
  const displayUnit = sellerItem.quantity?.unit ?? p.standardUnitsCode;
  const { widthInches, thicknessInches, lengthInches } =
    getPlatformProductDimensions(
      p,
      l,
      sellerItem.platformProductsLength?.isNominal
    );

  if (!lengthInches || !widthInches || !thicknessInches) return 0;

  const sellerStock = sellerItem.quantity?.value ?? 0;
  if (!sellerStock) return 0;

  // Convert the fill amount to the display unit if the vehicle payload is in a
  // different unit (e.g. capacity is in MBF but we display in BF).
  let rawQty = fillFraction * vehiclePayload.value;
  if (vehiclePayload.unit !== displayUnit) {
    try {
      rawQty = toStandardUnits(
        rawQty,
        vehiclePayload.unit,
        displayUnit,
        lengthInches,
        widthInches,
        thicknessInches
      );
    } catch {
      return 0;
    }
  }

  return Math.min(
    roundUpToBundleQty(
      rawQty,
      p.piecesPerBundle?.value ?? 0,
      displayUnit,
      lengthInches,
      widthInches,
      thicknessInches
    ),
    sellerStock
  );
}

function ItemCard({
  sellerItem,
  transport,
  totalLoads,
  selectedTransportVehicleId,
  sellerOrganizationFacilityId,
  locationId,
  locationName,
  cartId,
  parentOrderId,
}: Readonly<{
  sellerItem: SellerInventoryItem;
  transport: TransportMode | undefined;
  totalLoads: number;
  selectedTransportVehicleId: string;
  sellerOrganizationFacilityId: string;
  locationId: string;
  locationName: string;
  cartId: string;
  parentOrderId?: string | null;
}>) {
  const { t } = useTranslation('cart');
  const { mutate: updateCart, isPending } = useUpdateCart();
  const queryClient = useQueryClient();

  const productName =
    sellerItem.platformProductsLength?.platformProduct?.name ?? '';
  const lengthDisplay = sellerItem.platformProductsLength?.lengthDisplayValue
    ? formatLengthWithBasis(
        sellerItem.platformProductsLength.lengthDisplayValue,
        sellerItem.platformProductsLength.isNominal
      )
    : '';
  // quantity is already in USER_PREFERRED from the GraphQL query — use it directly.
  const stockUnit =
    sellerItem.quantity?.unit ??
    sellerItem.platformProductsLength?.platformProduct?.standardUnitsCode ??
    '';
  const sellerStock = sellerItem.quantity?.value ?? 0;
  const unitPrice = sellerItem.unitPrice;

  // Convert unit price to the display unit when it differs from the price unit.
  // Returns null when there's nothing to render (no unitPrice, or no listed price).
  // The non-null `price` local keeps TS narrowing across the IIFE's return paths.
  const displayUnitPrice = (() => {
    if (!unitPrice || unitPrice.price == null) return null;
    const price = unitPrice.price;
    const sameUnitFallback = { price, unit: unitPrice.unit };
    if (unitPrice.unit === stockUnit) return sameUnitFallback;
    const p = sellerItem.platformProductsLength?.platformProduct;
    const l = sellerItem.platformProductsLength?.length;
    if (!p) return sameUnitFallback;
    const { widthInches, thicknessInches, lengthInches } =
      getPlatformProductDimensions(
        p,
        l,
        sellerItem.platformProductsLength?.isNominal
      );
    if (!lengthInches || !widthInches || !thicknessInches) {
      return sameUnitFallback;
    }
    try {
      const convertedPrice =
        price.amount *
        toStandardUnits(
          1,
          stockUnit,
          unitPrice.unit,
          lengthInches,
          widthInches,
          thicknessInches
        );
      return {
        price: { amount: convertedPrice, currency: price.currency },
        unit: stockUnit,
      };
    } catch {
      return sameUnitFallback;
    }
  })();
  const grade = getPropertyStringSafe(
    sellerItem.platformProductsLength?.platformProduct?.properties,
    'grade'
  );
  const facilityName = locationName;

  const fillQty = calcFillQty(
    sellerItem,
    selectedTransportVehicleId,
    totalLoads
  );

  const targetLoads =
    partialLoadFraction(totalLoads) > 0
      ? Math.ceil(totalLoads)
      : Math.round(totalLoads) + 1;
  const transportLabel = transport
    ? {
        truck: t('cart.trucks_lowercase'),
        railcar: t('cart.railcars_lowercase'),
        container: t('cart.containers_lowercase'),
      }[transport]
    : undefined;

  const handleFill = () => {
    if (fillQty <= 0) return;
    updateCart(
      buildCartItemUpdateInput({
        productId: sellerItem.platformProductsLength.id,
        locationId,
        facilityId: sellerOrganizationFacilityId,
        quantity: fillQty,
        unit: stockUnit,
        parentOrderId,
      }),
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: ['additionalItems', cartId],
          });
        },
      }
    );
  };

  const stockHealth = sellerItem.stockHealth;
  const weeksCovered = sellerItem.weeksCovered;

  const statusLabelMap: Record<string, string> = {
    HEALTHY: t('cart.stock_health_healthy'),
    LOW: t('cart.stock_health_low'),
    CRITICAL: t('cart.stock_health_critical'),
  };
  const statusLabel =
    stockHealth != null ? (statusLabelMap[stockHealth] ?? null) : null;

  const statusClassMap: Record<string, string> = {
    HEALTHY: 'bg-success-100 text-success-700',
    LOW: 'bg-warning-100 text-warning-700',
    CRITICAL: 'bg-error-100 text-error-700',
  };
  const statusClass =
    stockHealth != null ? (statusClassMap[stockHealth] ?? '') : '';

  return (
    <div className="bg-neut-0 rounded-lg border border-neut-200 p-3 flex flex-col gap-2 hover:border-prim/40 hover:shadow-sm transition-all">
      {(statusLabel != null || weeksCovered != null) && (
        <div className="flex items-center justify-between gap-2">
          {statusLabel != null ? (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}
            >
              {statusLabel}
            </span>
          ) : (
            <span />
          )}
          {weeksCovered != null && (
            <span className="flex items-center gap-1 text-xs text-neut-400">
              <Clock className="w-2.5 h-2.5" aria-hidden="true" />
              {t('cart.weeks_left', { value: weeksCovered.toFixed(1) })}
            </span>
          )}
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-neut-prim leading-tight line-clamp-2">
          {productName}
        </p>
        <p className="text-xs text-neut-400 mt-0.5">
          {[
            grade ? `${t('cart.cart_grade')}: ${grade}` : null,
            lengthDisplay,
            facilityName,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="text-xs text-neut-400">
          {sellerStock} {stockUnit}
        </p>
      </div>
      <div className="flex items-center justify-between mt-auto pt-1.5 border-t border-neut-100">
        {displayUnitPrice && (
          <span className="text-xs text-neut-500">
            {formatCurrency(
              displayUnitPrice.price.amount,
              displayUnitPrice.price.currency
            )}
            /{displayUnitPrice.unit}
          </span>
        )}
        {fillQty > 0
          ? (() => {
              // Mirror Order Builder behaviour: when the seller has no listed
              // price, disable the Add button (backend rejects the cart-add
              // anyway with SellerProductNotPricedException). The tooltip lives
              // on the wrapping <span> because browsers suppress `title` on
              // disabled <button>s.
              const noListedPrice = displayUnitPrice == null;
              const enabledAriaLabel = transportLabel
                ? t('cart.add_to_fill_loads_aria_label', {
                    qty: fillQty.toFixed(2),
                    unit: stockUnit,
                    loads: targetLoads,
                    transport: transportLabel,
                  })
                : t('cart.add_to_fill_loads_aria_label_generic', {
                    qty: fillQty.toFixed(2),
                    unit: stockUnit,
                  });
              const ariaLabel = noListedPrice
                ? t('cart.add_disabled_no_listed_price')
                : enabledAriaLabel;
              return (
                <span
                  title={
                    noListedPrice
                      ? t('cart.add_disabled_no_listed_price')
                      : undefined
                  }
                  className="inline-flex"
                >
                  <button
                    type="button"
                    data-testid="additional-item-add"
                    className="flex items-center gap-1 px-2.5 py-1.5 bg-prim hover:bg-prim-hover disabled:opacity-50 text-neut-0 text-xs font-medium rounded-md transition-colors cursor-pointer disabled:cursor-not-allowed"
                    onClick={handleFill}
                    disabled={isPending || noListedPrice}
                    aria-label={ariaLabel}
                  >
                    <Plus className="w-2.5 h-2.5" aria-hidden="true" />
                    {t('cart.add')}
                  </button>
                </span>
              );
            })()
          : null}
      </div>
    </div>
  );
}

export function AdditionalItems({
  cartId,
  cartItems,
  locationId,
  locationName,
  sellerOrganizationId,
  sellerOrganizationFacilityId,
  transport,
  totalLoads,
  selectedTransportVehicleId,
  parentOrderId,
  incoTerm,
  cartCurrency,
  hasShippingRoute,
}: Readonly<AdditionalItemsProps>) {
  const { t } = useTranslation('cart');
  const [isOpen, setIsOpen] = useState(true);
  const panelId = useId();

  const { data } = useGetAdditionalProductsFromSeller(cartId);

  const items = (data?.additionalProductsFromSeller?.content ?? []).filter(
    (s): s is SellerInventoryItem => s !== null
  );

  if (!locationId) return null;

  return (
    <div className="lg:col-span-3 border-t border-dashed border-success-200 bg-success-50/30">
      <button
        className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-success-50/60 transition-colors"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-success-400" aria-hidden="true" />
          <span className="text-xs font-semibold text-prim">
            {transport
              ? {
                  truck: t('cart.fill_your_truck'),
                  railcar: t('cart.fill_your_railcar'),
                  container: t('cart.fill_your_container'),
                }[transport]
              : t('cart.fill_your_load')}
          </span>
          {items.length > 0 && (
            <span className="text-xs text-neut-500">
              {t('cart.top_needed_from_supplier')}
            </span>
          )}
        </div>
        <ChevronUp
          className={`w-3.5 h-3.5 text-neut-400 transition-transform ${isOpen ? '' : 'rotate-180'}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div id={panelId} className="px-6 pb-4 flex flex-col gap-4">
          {items.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((sellerItem) => (
                <ItemCard
                  key={sellerItem.id}
                  sellerItem={sellerItem}
                  transport={transport}
                  totalLoads={totalLoads}
                  selectedTransportVehicleId={selectedTransportVehicleId}
                  sellerOrganizationFacilityId={sellerOrganizationFacilityId}
                  locationId={locationId}
                  locationName={locationName}
                  cartId={cartId}
                  parentOrderId={parentOrderId}
                />
              ))}
            </div>
          )}
          <CartCatalogAddRow
            hasSuggestions={items.length > 0}
            cartId={cartId}
            cartItems={cartItems}
            locationId={locationId}
            sellerOrganizationId={sellerOrganizationId}
            sellerOrganizationFacilityId={sellerOrganizationFacilityId}
            transportVehicleId={selectedTransportVehicleId}
            parentOrderId={parentOrderId}
            incoTerm={incoTerm}
            cartCurrency={cartCurrency}
            hasShippingRoute={hasShippingRoute}
          />
        </div>
      )}
    </div>
  );
}
